import { useEffect, useState, useMemo } from "react"
import { Command } from "cmdk"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useGlobalSearch } from "@/hooks/use-search"
import {
  LayoutDashboard,
  Building2,
  Users,
  Kanban,
  CheckSquare,
  Calendar,
  Mail,
  FileText,
  Receipt,
  BarChart3,
  Plus,
  Search,
  Settings,
  Wrench,
  Loader2,
  Sparkles,
} from "lucide-react"
import { useAiStore } from "@/stores/ai-store"

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ENTITY_ICONS: Record<string, React.ElementType> = {
  Company: Building2,
  Contact: Users,
  Deal: Kanban,
  Task: CheckSquare,
  Quote: FileText,
  Invoice: Receipt,
  ServiceTicket: Wrench,
}

const ENTITY_LABELS: Record<string, string> = {
  Company: "Firmy",
  Contact: "Kontakty",
  Deal: "Transakcje",
  Task: "Zadania",
  Quote: "Oferty",
  Invoice: "Faktury",
  ServiceTicket: "Zgłoszenia serwisowe",
}

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { open: openAi } = useAiStore()
  const [inputValue, setInputValue] = useState("")
  const debouncedQuery = useDebounce(inputValue, 300)
  const { data: searchData, isLoading: isSearching } = useGlobalSearch(debouncedQuery)

  const isSearchMode = inputValue.length >= 2
  const searchResults = searchData?.data ?? []

  // Group search results by entity type
  const groupedResults = useMemo(() => {
    const groups: Record<string, typeof searchResults> = {}
    for (const result of searchResults) {
      if (!groups[result.entity_type]) groups[result.entity_type] = []
      groups[result.entity_type].push(result)
    }
    return groups
  }, [searchResults])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault()
        onOpenChange(!open)
      }
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [open, onOpenChange])

  // Reset input when closing
  useEffect(() => {
    if (!open) setInputValue("")
  }, [open])

  const go = (path: string) => {
    navigate(path)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[20vh]">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={() => onOpenChange(false)}
      />

      {/* Dialog */}
      <Command
        className="relative w-full max-w-[640px] overflow-hidden rounded-lg border border-border bg-popover shadow-lg"
        shouldFilter={!isSearchMode}
      >
        <div className="flex items-center border-b border-border">
          <Search size={16} className="ml-4 text-muted-foreground" />
          <Command.Input
            placeholder={t("commandPalette.placeholder")}
            value={inputValue}
            onValueChange={setInputValue}
            className="h-11 w-full bg-transparent px-3 text-[13px] text-foreground outline-none"
          />
          {isSearching && isSearchMode && (
            <Loader2 size={16} className="mr-4 animate-spin text-muted-foreground" />
          )}
        </div>
        <Command.List className="max-h-[50vh] overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
            {isSearchMode ? "Brak wyników wyszukiwania" : t("common.noResults")}
          </Command.Empty>

          {isSearchMode ? (
            /* Search results grouped by entity type */
            Object.entries(groupedResults).map(([entityType, results]) => {
              const Icon = ENTITY_ICONS[entityType] || FileText
              const label = ENTITY_LABELS[entityType] || entityType

              return (
                <Command.Group
                  key={entityType}
                  heading={label}
                  className="text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
                >
                  {results.map((result) => (
                    <Command.Item
                      key={`${result.entity_type}-${result.entity_id}`}
                      value={`${result.title} ${result.subtitle || ""}`}
                      onSelect={() => go(result.url)}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm text-muted-foreground aria-selected:bg-muted"
                    >
                      <Icon size={16} className="flex-shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-foreground">
                          {result.title}
                        </div>
                        {result.subtitle && (
                          <div className="truncate text-xs text-muted-foreground">
                            {result.subtitle}
                          </div>
                        )}
                      </div>
                    </Command.Item>
                  ))}
                </Command.Group>
              )
            })
          ) : (
            /* Default navigation + create commands */
            <>
              <Command.Group
                heading={t("commandPalette.navigation")}
                className="text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
              >
                <Command.Item
                  onSelect={() => {
                    openAi()
                    onOpenChange(false)
                  }}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm text-blue-600 dark:text-blue-400 aria-selected:bg-muted"
                >
                  <Sparkles size={16} />
                  {t("ai.askAi")}
                  <kbd className="ml-auto rounded-md border border-border bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                    ⌘J
                  </kbd>
                </Command.Item>
                {[
                  { icon: LayoutDashboard, label: t("nav.dashboard"), path: "/dashboard" },
                  { icon: Kanban, label: t("nav.pipeline"), path: "/deals" },
                  { icon: Building2, label: t("nav.companies"), path: "/companies" },
                  { icon: Users, label: t("nav.contacts"), path: "/contacts" },
                  { icon: CheckSquare, label: t("nav.tasks"), path: "/tasks" },
                  { icon: Calendar, label: t("nav.calendar"), path: "/calendar" },
                  { icon: Mail, label: t("nav.email"), path: "/mail" },
                  { icon: FileText, label: t("nav.quotes"), path: "/quotes" },
                  { icon: Receipt, label: t("nav.invoices"), path: "/invoices" },
                  { icon: BarChart3, label: t("nav.reports"), path: "/reports" },
                  { icon: Settings, label: t("nav.settings"), path: "/settings" },
                ].map(({ icon: Icon, label, path }) => (
                  <Command.Item
                    key={path}
                    onSelect={() => go(path)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm text-muted-foreground aria-selected:bg-muted"
                  >
                    <Icon size={16} />
                    {label}
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Group
                heading={t("commandPalette.create")}
                className="text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider"
              >
                {[
                  { label: t("companies.new"), path: "/companies/new" },
                  { label: t("contacts.new"), path: "/contacts/new" },
                  { label: t("deals.new"), path: "/deals/new" },
                  { label: t("tasks.new"), path: "/tasks/new" },
                ].map(({ label, path }) => (
                  <Command.Item
                    key={path}
                    onSelect={() => go(path)}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm text-muted-foreground aria-selected:bg-muted"
                  >
                    <Plus size={16} />
                    {label}
                  </Command.Item>
                ))}
              </Command.Group>
            </>
          )}
        </Command.List>

        {/* Footer with keyboard hints */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>↑↓ nawigacja</span>
            <span>↵ wybierz</span>
            <span>esc zamknij</span>
          </div>
          {isSearchMode && (
            <span>{searchResults.length} {searchResults.length === 1 ? "wynik" : "wyników"}</span>
          )}
        </div>
      </Command>
    </div>
  )
}
